import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote, Loader2, Upload, ArrowRight, SkipForward, Plus } from "lucide-react";
import { toast } from "sonner";
import { BankStatement } from "@/lib/api";
import { filesToOcrPayload } from "@/lib/multi-file";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const BankStatementPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [transactions, setTransactions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const [skipped, setSkipped] = useState([]);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    try {
      const p = await filesToOcrPayload(fileList);
      toast.message(
        `Reading ${p.images.length} page${p.images.length === 1 ? "" : "s"}…`,
      );
      const res = await BankStatement.parse({ images: p.images });
      // Only "out" transactions are bill candidates
      const outs = (res.transactions || []).filter((t) => t.direction === "out");
      setTransactions(outs);
      setIdx(0);
      setSkipped([]);
      toast.success(
        `Found ${outs.length} outgoing transaction${outs.length === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      toast.error(e?.message || "Could not read the statement.");
    } finally {
      setBusy(false);
    }
  };

  const tx = transactions[idx];
  const total = transactions.length;
  const done = idx >= total;

  const allocate = () => {
    if (!tx) return;
    navigate("/allocate", { state: { tx } });
  };
  const asBill = () => {
    if (!tx) return;
    navigate("/new", { state: { prefill: { company: tx.counterparty, amount_due: tx.amount, due_date: tx.date, notes: tx.description } } });
  };
  const asReceipt = () => {
    if (!tx) return;
    navigate("/receipts/new", { state: { prefill: { vendor: tx.counterparty, total_amount: tx.amount, receipt_date: tx.date, notes: tx.description } } });
  };
  const skip = () => {
    setSkipped([...skipped, tx]);
    setIdx(idx + 1);
  };

  return (
    <div data-testid="bank-statement-page" className="fade-rise">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <Banknote className="h-3 w-3" /> Bank Statement
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight leading-tight">
        Reconcile your statement
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Upload last month's bank statement (PDF or multi-page screenshot). Billy
        reads every transaction and walks you through them one-by-one. Match →
        Skip → New Bill — your call.
      </p>

      {transactions.length === 0 ? (
        <div className="mt-10 dropzone">
          <div className="flex flex-col items-center gap-6">
            <Banknote className="h-10 w-10" strokeWidth={1.5} />
            <div className="text-center">
              <div className="font-display font-extrabold text-2xl">
                Upload statement
              </div>
              <div className="text-neutral-500 text-sm mt-1">
                Multi-page PDF or screenshots — up to 25 pages.
              </div>
            </div>
            <button
              data-testid="upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? "Reading…" : "Upload statement"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              data-testid="file-input"
              className="hidden"
              onChange={(e) => upload(e.target.files)}
            />
          </div>
        </div>
      ) : done ? (
        <div className="mt-10 border-2 border-black p-8 text-center" data-testid="all-done">
          <div className="font-display font-black text-3xl">All done 🎉</div>
          <div className="text-sm text-neutral-600 mt-2">
            {total - skipped.length} processed · {skipped.length} skipped
          </div>
          <div className="mt-6 flex justify-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => {
                setTransactions([]);
                setIdx(0);
                setSkipped([]);
              }}
              className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-black hover:text-white transition-colors"
            >
              Upload another
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-10 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
            Transaction {idx + 1} of {total}
          </div>
          <div className="border-2 border-black p-6 bg-white" data-testid="tx-card">
            <div className="font-display font-black text-4xl mono">{fmt(tx.amount)}</div>
            <div className="text-sm text-neutral-700 mt-2">{tx.description || tx.counterparty || "—"}</div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] uppercase tracking-widest">
              <div>
                <div className="text-neutral-500">Date</div>
                <div className="font-bold mono">{tx.date || "—"}</div>
              </div>
              <div>
                <div className="text-neutral-500">Reference</div>
                <div className="font-bold mono break-all">{tx.reference || "—"}</div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              data-testid="allocate-btn"
              onClick={allocate}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center justify-center gap-2"
            >
              <ArrowRight className="h-4 w-4" /> Allocate to bill
            </button>
            <button
              data-testid="skip-btn"
              onClick={skip}
              className="border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors inline-flex items-center justify-center gap-2"
            >
              <SkipForward className="h-4 w-4" /> Skip
            </button>
            <button
              data-testid="new-bill-btn"
              onClick={asBill}
              className="border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Save as new Bill
            </button>
            <button
              data-testid="new-receipt-btn"
              onClick={asReceipt}
              className="border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Save as Receipt
            </button>
          </div>

          <div className="text-[11px] text-neutral-500 mt-4">
            Tip: tap Allocate to match this transaction against your outstanding
            bills (split across multiple is supported). Skip if you've already
            recorded it.
          </div>
        </div>
      )}
    </div>
  );
};

export default BankStatementPage;
